import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderKanban, Plus, Trash2, Loader2, MapPin, Layers3, FileCode2, Mountain, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { listProjects, createProject, deleteProject, listFiles, listSurfaces, listStandards } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";

const NONE = "none";

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [surfaces, setSurfaces] = useState([]);
  const [standards, setStandards] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { activeId, setActiveId, refreshProjects } = useProject();
  const [form, setForm] = useState({
    name: "", standard_id: NONE, description: "",
    design_file_id: NONE, survey_file_id: NONE,
    existing_surface_id: NONE, design_surface_id: NONE, notes: "",
  });

  const refresh = () => listProjects().then(setProjects);
  useEffect(() => {
    refresh();
    listFiles().then(setFiles);
    listSurfaces().then(setSurfaces);
    listStandards().then(setStandards);
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setInput = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), description: form.description, notes: form.notes };
      ["standard_id", "design_file_id", "survey_file_id", "existing_surface_id", "design_surface_id"].forEach((k) => {
        if (form[k] && form[k] !== NONE) payload[k] = form[k];
      });
      const created = await createProject(payload);
      toast.success(`Project "${form.name}" created & set active`);
      setActiveId(created.id);
      refreshProjects();
      setOpen(false);
      setForm({ name: "", standard_id: NONE, description: "", design_file_id: NONE, survey_file_id: NONE, existing_surface_id: NONE, design_surface_id: NONE, notes: "" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create project");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => { await deleteProject(id); refresh(); refreshProjects(); toast.success("Project removed"); };

  const fileName = (id) => files.find((f) => f.id === id)?.name;
  const surfName = (id) => surfaces.find((s) => s.id === id)?.name;
  const stdName = (id) => standards.find((s) => s.id === id)?.name;

  const sel = (label, value, onChange, items, placeholder, testid) => (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm max-h-64">
          <SelectItem value={NONE} className="font-mono text-xs">— none —</SelectItem>
          {items.map((it) => <SelectItem key={it.id} value={it.id} className="font-mono text-xs">{it.name}{it.role ? ` (${it.role})` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-10">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Projects</h1>
          <p className="font-mono text-[11px] text-zinc-500">Group an LGA standard, design/as-built files, reference surfaces &amp; templates — all optional</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-project-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white"><Plus className="w-4 h-4 mr-2" /> New Project</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-sm max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">New Project</DialogTitle>
              <DialogDescription className="font-mono text-xs text-zinc-500">Only the name is required. Everything else can be attached later.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Project name *</label>
                <Input value={form.name} onChange={setInput("name")} placeholder="e.g. Riverbend Estate Stage 3" data-testid="project-name" className="h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sel("Authority / standard", form.standard_id, set("standard_id"), standards, "Standard…", "project-standard")}
                {sel("Design / construction XML", form.design_file_id, set("design_file_id"), files, "Design file…", "project-design")}
                {sel("As-constructed / survey XML", form.survey_file_id, set("survey_file_id"), files, "Survey file…", "project-survey")}
                <div />
                {sel("Existing ground surface", form.existing_surface_id, set("existing_surface_id"), surfaces, "Existing TIN…", "project-existing-surface")}
                {sel("Design surface", form.design_surface_id, set("design_surface_id"), surfaces, "Design TIN…", "project-design-surface")}
              </div>
              <div className="space-y-1.5">
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Notes</label>
                <Input value={form.notes} onChange={setInput("notes")} placeholder="Optional notes" data-testid="project-notes" className="h-9 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-xs" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={busy} data-testid="save-project-btn" className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Create project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="p-6">
        {projects.length === 0 ? (
          <div className="grid-texture border border-zinc-800 rounded-sm py-20 flex flex-col items-center text-center" data-testid="projects-empty">
            <FolderKanban className="w-10 h-10 text-zinc-700 mb-4" />
            <p className="text-sm text-zinc-500 max-w-md">No projects yet. Create one to bundle a council standard, design/as-built files, reference surfaces and templates in a single workspace.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="projects-list">
            {projects.map((p) => (
              <div key={p.id} data-testid={`project-${p.id}`} className={`border rounded-sm bg-[#101010] p-4 space-y-3 ${activeId === p.id ? "border-blue-600/50 ring-1 ring-blue-600/30" : "border-zinc-800"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderKanban className={`w-5 h-5 shrink-0 ${activeId === p.id ? "text-blue-400" : "text-zinc-400"}`} />
                    <div className="min-w-0">
                      <div className="font-display text-sm font-semibold text-zinc-100 truncate">{p.name}</div>
                      {p.standard_id && <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {stdName(p.standard_id) || p.standard_id}</div>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`} className="h-7 w-7 p-0 rounded-sm text-zinc-500 hover:text-red-400 hover:bg-zinc-900"><Trash2 className="w-4 h-4" /></Button>
                </div>
                {p.description && <p className="font-mono text-[11px] text-zinc-400">{p.description}</p>}
                <div className="space-y-1.5 font-mono text-[10px] text-zinc-500">
                  {p.design_file_id && <Row icon={FileCode2} label="Design" value={fileName(p.design_file_id)} />}
                  {p.survey_file_id && <Row icon={FileCode2} label="Survey" value={fileName(p.survey_file_id)} />}
                  {p.existing_surface_id && <Row icon={Mountain} label="Existing" value={surfName(p.existing_surface_id)} />}
                  {p.design_surface_id && <Row icon={Layers3} label="Design TIN" value={surfName(p.design_surface_id)} />}
                </div>
                {p.notes && <p className="font-mono text-[10px] text-zinc-600 border-t border-zinc-900 pt-2">{p.notes}</p>}
                <Button
                  size="sm"
                  onClick={() => { setActiveId(activeId === p.id ? "" : p.id); toast.success(activeId === p.id ? "Project deactivated" : `"${p.name}" is now active — files auto-load across QA, Profile & Surfaces`); }}
                  data-testid={`activate-project-${p.id}`}
                  className={`w-full h-8 rounded-sm font-mono text-[11px] ${activeId === p.id ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-300" : "bg-blue-600 hover:bg-blue-500 text-white"}`}
                >
                  <Power className="w-3.5 h-3.5 mr-1.5" /> {activeId === p.id ? "Active — click to deactivate" : "Set as active project"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3 h-3 text-zinc-600" />
      <span className="text-zinc-600 w-16">{label}</span>
      <span className="text-zinc-300 truncate">{value || "—"}</span>
    </div>
  );
}
